/*
 * Kutify Registriamet — storefront behaviour.
 *
 * Everything here is a custom element that upgrades markup already rendered by
 * Liquid. Nothing builds UI from scratch in the browser: the cart drawer and
 * the predictive search panel are re-rendered server-side through Shopify's
 * Section Rendering API, so prices, translations and money formatting stay in
 * Liquid where they belong.
 *
 * Loaded with `defer`, so the DOM is parsed before any of this runs.
 */
(function () {
  'use strict';

  var Kutify = window.Kutify || {};
  var routes = Kutify.routes || {};
  var strings = Kutify.strings || {};

  /* ------------------------------------------------------------- helpers -- */

  var FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function focusables(root) {
    return Array.prototype.filter.call(root.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
  }

  /**
   * Keeps Tab inside `root` until released. Returns a teardown function.
   * Used by the cart drawer, the mobile menu and the modals.
   */
  function trapFocus(root, initial) {
    var previous = document.activeElement;

    function onKeydown(event) {
      if (event.key !== 'Tab') return;
      var items = focusables(root);
      if (!items.length) return;

      var first = items[0];
      var last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeydown);

    var target = initial || focusables(root)[0];
    if (target) target.focus();

    return function release() {
      document.removeEventListener('keydown', onKeydown);
      if (previous && typeof previous.focus === 'function') previous.focus();
    };
  }

  function lockScroll(locked) {
    document.documentElement.style.overflow = locked ? 'hidden' : '';

    /*
     * The announcement ticker animates a transform, which puts it on its own
     * composited layer, and iOS Safari paints that layer above a fixed overlay
     * however the z-index reads — the mobile menu covers the page and the strip
     * still scrolls across its heading and close button. Every caller of this
     * helper covers the page, so the strip is taken out of paint for the
     * duration instead of being fought with stacking rules the compositor is
     * free to ignore.
     */
    document.documentElement.classList.toggle('ktf-scroll-locked', locked);
  }

  /**
   * How much of the announcement strip is still on screen, in px.
   *
   * The strip is the page's own bar and the compositor paints it over any fixed
   * overlay, so an overlay that keeps it visible has to begin below it. That is
   * its full height at rest and nothing once the page has scrolled past it.
   * Called after the overlay's class is on the document, so the separating rule
   * that class adds is inside the measurement.
   */
  function announcementOffset() {
    var strip = document.querySelector('.ktf-announcement');
    if (!strip) return 0;

    var rect = strip.getBoundingClientRect();
    var showing = Math.min(Math.max(rect.bottom, 0), rect.height);
    return showing > 0 ? Math.round(showing) : 0;
  }

  function debounce(fn, wait) {
    var timer;
    return function () {
      var args = arguments;
      var self = this;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(self, args);
      }, wait);
    };
  }

  /** Replaces a rendered `shopify-section-*` wrapper with fresh markup. */
  function swapSection(id, html) {
    var current = document.getElementById('shopify-section-' + id);
    if (!current || !html) return null;

    var holder = document.createElement('div');
    holder.innerHTML = html;
    var fresh = holder.querySelector('#shopify-section-' + id) || holder.firstElementChild;
    if (!fresh) return null;

    current.replaceWith(fresh);
    return fresh;
  }

  /* ---------------------------------------------------------------- cart -- */

  var Cart = {
    /** Broadcasts so any listening element (header count, drawer) can react. */
    publish: function (detail) {
      document.dispatchEvent(new CustomEvent('ktf:cart:updated', { detail: detail }));
    },

    syncCount: function (count) {
      if (typeof count !== 'number') return;

      document.querySelectorAll('[data-cart-count]').forEach(function (el) {
        el.textContent = count;
      });
    },

    /**
     * @param {FormData} formData - a product form, already containing `id`.
     * @returns {Promise<object>} resolves with the parsed response.
     */
    add: function (formData) {
      formData.append('sections', 'cart-drawer');
      formData.append('sections_url', window.location.pathname);

      return fetch(routes.cart_add, {
        method: 'POST',
        headers: { Accept: 'application/javascript' },
        body: formData
      })
        .then(function (response) {
          return response.json().then(function (data) {
            if (!response.ok) {
              // Shopify returns 422 with a `description` for sold-out and
              // quantity-limit rejections — surface that, not a generic error.
              throw new Error(data.description || data.message || strings.addError);
            }
            return data;
          });
        })
        .then(function (data) {
          Cart.render(data.sections);
          return data;
        });
    },

    /**
     * Several variants in one request, for the bundle. /cart/add.js takes an
     * `items` array as JSON — posting them one at a time would re-render the
     * drawer once per product and let a mid-way failure leave half a bundle
     * in the cart.
     *
     * @param {Array<{id: string, quantity: number}>} items
     */
    addItems: function (items) {
      return fetch(routes.cart_add, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/javascript'
        },
        body: JSON.stringify({
          items: items,
          sections: 'cart-drawer',
          sections_url: window.location.pathname
        })
      })
        .then(function (response) {
          return response.json().then(function (data) {
            if (!response.ok) {
              throw new Error(data.description || data.message || strings.addError);
            }
            return data;
          });
        })
        .then(function (data) {
          Cart.render(data.sections);
          return data;
        });
    },

    /**
     * @param {object} body - `{ line, quantity }` or `{ note }`.
     */
    change: function (body) {
      body.sections = 'cart-drawer';
      body.sections_url = window.location.pathname;

      return fetch(routes.cart_change, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/javascript'
        },
        body: JSON.stringify(body)
      })
        .then(function (response) {
          return response.json().then(function (data) {
            if (!response.ok) throw new Error(data.description || strings.cartError);
            return data;
          });
        })
        .then(function (cart) {
          Cart.render(cart.sections);
          Cart.syncCount(cart.item_count);
          Cart.publish({ cart: cart });
          return cart;
        });
    },

    updateNote: function (note) {
      return fetch(routes.cart_update, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify({ note: note })
      });
    },

    /** Swaps in the freshly rendered drawer and re-reads the item count. */
    render: function (sections) {
      if (!sections || !sections['cart-drawer']) return;

      var fresh = swapSection('cart-drawer', sections['cart-drawer']);
      if (!fresh) return;

      var drawer = fresh.querySelector('ktf-cart-drawer');
      if (drawer) {
        var count = parseInt(drawer.getAttribute('data-item-count'), 10);
        if (!isNaN(count)) Cart.syncCount(count);
      }
    }
  };

  /* -------------------------------------------------------- cart drawer --- */

  /**
   * The "Toimiku sahtel". Re-rendered wholesale by the Cart API, so all state
   * that must survive a swap lives on the element's attributes, not in JS.
   */
  var CartDrawer = {
    open: function () {
      var el = document.querySelector('ktf-cart-drawer');
      if (!el) return;

      /* The strip stays visible here too; the panel starts below it, with the
         strip's own rule as the seam between them. The value goes on the
         document, not on the drawer: the Cart API replaces this element
         wholesale on every update, which would take an inline property with it. */
      document.documentElement.classList.add('ktf-cart-open');
      document.documentElement.style.setProperty('--ktf-overlay-top', announcementOffset() + 'px');

      el.hidden = false;
      lockScroll(true);
      el._release = trapFocus(el, el.querySelector('[data-cart-close]'));
    },

    close: function () {
      var el = document.querySelector('ktf-cart-drawer');
      if (!el || el.hidden) return;

      el.hidden = true;
      document.documentElement.classList.remove('ktf-cart-open');
      lockScroll(false);
      if (el._release) {
        el._release();
        el._release = null;
      }
    },

    isOpen: function () {
      var el = document.querySelector('ktf-cart-drawer');
      return !!el && !el.hidden;
    }
  };

  // Delegated, because the drawer's contents are replaced on every change.
  document.addEventListener('click', function (event) {
    var close = event.target.closest('[data-cart-close]');
    if (close) {
      event.preventDefault();
      CartDrawer.close();
      return;
    }

    var remove = event.target.closest('[data-line-remove]');
    if (remove) {
      event.preventDefault();
      Cart.change({ line: parseInt(remove.getAttribute('data-line'), 10), quantity: 0 }).catch(
        function (error) {
          announce(error.message);
        }
      );
      return;
    }

    // The bundle adds the companions plus whatever variant the picker is
    // currently on — read from the form at click time, not baked into the
    // markup, so a customer who changed size gets the size they chose.
    var bundle = event.target.closest('[data-bundle-add]');
    if (bundle) {
      event.preventDefault();
      // The buy form belongs to the product section, which has its own id —
      // the bundle is a separate section, so its id cannot name that form.
      // Find the form by what it is instead of by a guessed id.
      var chosen = document.querySelector('.ktf-buy__form [name="id"]');
      var items = [];
      if (chosen && chosen.value) items.push({ id: chosen.value, quantity: 1 });
      (bundle.getAttribute('data-bundle-add') || '')
        .split(',')
        .filter(Boolean)
        .forEach(function (id) {
          items.push({ id: id, quantity: 1 });
        });
      if (!items.length) return;

      bundle.setAttribute('aria-disabled', 'true');
      Cart.addItems(items)
        .then(function () {
          bundle.removeAttribute('aria-disabled');
          if (Kutify.cartType === 'drawer') CartDrawer.open();
        })
        .catch(function (error) {
          bundle.removeAttribute('aria-disabled');
          announce(error.message);
        });
      return;
    }

    // Upsell rows post a bare variant id; Cart.add re-renders the drawer, so
    // the row removes itself once the product is in the cart.
    var upsell = event.target.closest('[data-upsell-add]');
    if (upsell) {
      event.preventDefault();
      upsell.setAttribute('aria-disabled', 'true');
      var payload = new FormData();
      payload.append('id', upsell.getAttribute('data-upsell-add'));
      payload.append('quantity', '1');
      Cart.add(payload).catch(function (error) {
        upsell.removeAttribute('aria-disabled');
        announce(error.message);
      });
      return;
    }

    var step = event.target.closest('[data-qty-up], [data-qty-down]');
    if (step) {
      event.preventDefault();
      var wrap = step.closest('[data-qty]');
      var input = wrap && wrap.querySelector('[data-qty-input]');
      if (!input) return;

      var next = parseInt(input.value, 10) + (step.hasAttribute('data-qty-up') ? 1 : -1);
      var max = input.getAttribute('max');
      if (next < 0) next = 0;
      if (max && next > parseInt(max, 10)) next = parseInt(max, 10);

      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    // Opening the cart drawer instead of following the /cart link.
    var cartLink = event.target.closest('[data-cart-link]');
    if (cartLink && Kutify.cartType === 'drawer' && document.querySelector('ktf-cart-drawer')) {
      event.preventDefault();
      CartDrawer.open();
    }
  });

  document.addEventListener('change', function (event) {
    var qty = event.target.closest('[data-qty-input]');
    if (qty && qty.closest('ktf-cart-drawer, [data-cart-page]')) {
      Cart.change({
        line: parseInt(qty.getAttribute('data-line'), 10),
        quantity: parseInt(qty.value, 10)
      }).catch(function (error) {
        announce(error.message);
      });
    }
  });

  document.addEventListener(
    'input',
    debounce(function (event) {
      var note = event.target.closest('[data-cart-note]');
      if (note) Cart.updateNote(note.value);
    }, 500)
  );

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    if (CartDrawer.isOpen()) CartDrawer.close();
  });

  /** Writes into the drawer's polite live region, if it is present. */
  function announce(message) {
    var status = document.querySelector('[data-cart-status]');
    if (status) status.textContent = message;
  }

  /* -------------------------------------------------------------- header -- */

  class KtfHeader extends HTMLElement {
    connectedCallback() {
      this.menu = this.querySelector('#ktf-menu-drawer');
      this.menuToggle = this.querySelector('[data-menu-toggle]');
      this.searchPanel = this.querySelector('#ktf-search-panel');
      this.searchToggle = this.querySelector('[data-search-toggle]');

      if (this.menuToggle) {
        this.menuToggle.addEventListener('click', this.toggleMenu.bind(this));
      }

      this.querySelectorAll('[data-menu-close]').forEach(
        function (button) {
          button.addEventListener('click', this.closeMenu.bind(this));
        }.bind(this)
      );

      if (this.searchToggle) {
        this.searchToggle.addEventListener('click', this.toggleSearch.bind(this));
      }

      document.addEventListener('keydown', this.onKeydown.bind(this));

      // Only one nav dropdown stays open at a time.
      this.querySelectorAll('[data-nav-details]').forEach(
        function (details) {
          details.addEventListener('toggle', function () {
            if (!details.open) return;
            this.querySelectorAll('[data-nav-details]').forEach(function (other) {
              if (other !== details) other.open = false;
            });
          }.bind(this));
        }.bind(this)
      );

      document.addEventListener('click', this.onDocumentClick.bind(this));

      // Condense the sticky header once the page has scrolled past the fold's
      // first slice, so the pinned bar stays small instead of holding the
      // whole header on screen.
      if (this.classList.contains('ktf-header--sticky')) {
        // Give the logo concrete values to animate between. Its resting height
        // comes from its width, and `auto` cannot be transitioned, so measure
        // the natural size once and hand the numbers to CSS.
        var logo = this.querySelector('.ktf-header__logo');
        if (logo) {
          var measureLogo = function () {
            var wasCondensed = this.classList.contains('is-condensed');
            if (wasCondensed) this.classList.remove('is-condensed');
            this.classList.remove('is-measured');
            var rect = logo.getBoundingClientRect();
            if (rect.height > 0 && rect.width > 0) {
              this.style.setProperty('--ktf-logo-h', Math.round(rect.height) + 'px');
              this.style.setProperty('--ktf-logo-ar', rect.width / rect.height);
              this.classList.add('is-measured');
            }
            if (wasCondensed) this.classList.add('is-condensed');
          }.bind(this);

          if (logo.complete) measureLogo();
          else logo.addEventListener('load', measureLogo);
          window.addEventListener('resize', debounce(measureLogo, 150), { passive: true });
        }

        // The header pins below the announcement, so it needs that bar's
        // height. Measured rather than assumed, since the copy can wrap.
        var announcement = document.querySelector('.ktf-announcement');
        if (announcement) {
          var measureAnnouncement = function () {
            var h = Math.round(announcement.getBoundingClientRect().height);
            if (h > 0) document.documentElement.style.setProperty('--ktf-ann-h', h + 'px');
          };
          measureAnnouncement();
          window.addEventListener('resize', debounce(measureAnnouncement, 150), { passive: true });
        }

        this.onScroll = function () {
          this.classList.toggle('is-condensed', window.scrollY > 80);
        }.bind(this);
        this.onScroll();
        window.addEventListener('scroll', this.onScroll, { passive: true });
      }
    }

    disconnectedCallback() {
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    }

    toggleMenu() {
      if (!this.menu) return;
      this.menu.hidden ? this.openMenu() : this.closeMenu();
    }

    openMenu() {
      /*
       * The strip stays visible over the menu, so the drawer's head is cleared
       * out from under it: the strip's visible height plus a gap. Measured once
       * — the page cannot move while the menu is open.
       */
      document.documentElement.classList.add('ktf-menu-open');
      var clear = announcementOffset();
      document.documentElement.style.setProperty(
        '--ktf-overlay-top',
        clear > 0 ? clear + 12 + 'px' : '0px'
      );

      this.menu.hidden = false;
      this.menuToggle.setAttribute('aria-expanded', 'true');
      lockScroll(true);
      this._release = trapFocus(this.menu);
    }

    closeMenu() {
      if (!this.menu || this.menu.hidden) return;
      this.menu.hidden = true;
      this.menuToggle.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('ktf-menu-open');
      lockScroll(false);
      if (this._release) {
        this._release();
        this._release = null;
      }
    }

    toggleSearch() {
      if (!this.searchPanel) return;

      var opening = this.searchPanel.hidden;
      this.searchPanel.hidden = !opening;
      this.searchToggle.setAttribute('aria-expanded', String(opening));

      if (opening) {
        var input = this.searchPanel.querySelector('[data-search-input]');
        if (input) input.focus();
      }
    }

    closeSearch() {
      if (!this.searchPanel || this.searchPanel.hidden) return;
      this.searchPanel.hidden = true;
      this.searchToggle.setAttribute('aria-expanded', 'false');
    }

    onKeydown(event) {
      if (event.key !== 'Escape') return;
      this.closeMenu();
      if (this.searchPanel && !this.searchPanel.hidden) {
        this.closeSearch();
        this.searchToggle.focus();
      }
    }

    onDocumentClick(event) {
      // Clicking outside a nav dropdown closes it.
      if (!event.target.closest('[data-nav-details]')) {
        this.querySelectorAll('[data-nav-details]').forEach(function (details) {
          details.open = false;
        });
      }
    }
  }

  /* ---------------------------------------------------- predictive search - */

  class KtfSearch extends HTMLElement {
    connectedCallback() {
      if (!this.hasAttribute('data-predictive')) return;

      this.input = this.querySelector('[data-search-input]');
      this.results = this.querySelector('[data-search-results]');
      this.status = this.querySelector('[data-search-status]');
      if (!this.input || !this.results) return;

      this.index = -1;

      this.input.addEventListener('input', debounce(this.search.bind(this), 250));
      this.input.addEventListener('keydown', this.onKeydown.bind(this));
      document.addEventListener('click', this.onDocumentClick.bind(this));
    }

    search() {
      var term = this.input.value.trim();

      if (term.length < 2) {
        this.hide();
        return;
      }

      var url =
        routes.predictive_search +
        '?q=' +
        encodeURIComponent(term) +
        '&section_id=predictive-search' +
        '&resources[type]=product,collection,page' +
        '&resources[limit]=6';

      // Abort an in-flight request so a slow early keystroke cannot overwrite
      // the results of a later one.
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      fetch(url, { signal: this.controller.signal })
        .then(function (response) {
          if (!response.ok) throw new Error('search failed');
          return response.text();
        })
        .then(
          function (html) {
            var holder = document.createElement('div');
            holder.innerHTML = html;
            var root = holder.querySelector('[data-predictive-root]');
            if (!root) return;

            this.results.innerHTML = root.outerHTML;
            var extra = this.register(term, this.results.querySelector('[data-predictive-root]'));
            this.show((parseInt(root.getAttribute('data-result-count'), 10) || 0) + extra);
          }.bind(this)
        )
        .catch(function (error) {
          if (error.name !== 'AbortError') this.hide();
        }.bind(this));
    }

    /*
     * The register's own index, from the lines rendered beside this field.
     *
     * Shopify's predictive search only knows products, collections and pages,
     * and the lore lives in section settings it cannot see — so "vorm 51-K"
     * would come back empty from the API and still has to answer here. Entries
     * without a url are hints: rendered as a finding, deliberately not a link.
     */
    register(term, root) {
      if (!root) return 0;

      var source = document.querySelector('[data-register-index]');
      if (!source) return 0;

      var needle = term.toLowerCase().trim();
      if (!needle) return 0;

      var hits = source.textContent
        .split('\n')
        .map(function (line) {
          return line.trim();
        })
        .filter(Boolean)
        .map(function (line) {
          var parts = line.split('|');
          return {
            title: (parts[0] || '').trim(),
            url: (parts[1] || '').trim(),
            aliases: (parts[2] || '').trim(),
            kicker: (parts[3] || '').trim()
          };
        })
        .filter(function (entry) {
          return (entry.title + ',' + entry.aliases).toLowerCase().indexOf(needle) !== -1;
        })
        .slice(0, 5);

      if (!hits.length) return 0;

      var group = document.createElement('div');
      group.className = 'ktf-psearch__group';

      var heading = document.createElement('p');
      heading.className = 'ktf-label ktf-psearch__heading';
      heading.textContent = strings.registerHeading || '';
      group.appendChild(heading);

      hits.forEach(function (entry) {
        // textContent throughout: these lines are theme copy, but building
        // markup out of strings is how an index becomes an injection.
        var row = document.createElement(entry.url ? 'a' : 'div');
        row.className = 'ktf-psearch__item ktf-psearch__item--plain';
        if (entry.url) {
          row.setAttribute('href', entry.url);
          row.setAttribute('role', 'option');
          row.setAttribute('tabindex', '-1');
        }

        var title = document.createElement('span');
        title.className = 'ktf-psearch__title';
        title.textContent = entry.title;
        row.appendChild(title);

        var note = document.createElement('span');
        note.className = 'ktf-label ktf-psearch__note';
        note.textContent = entry.kicker;
        row.appendChild(note);

        group.appendChild(row);
      });

      root.insertBefore(group, root.firstChild);
      return hits.length;
    }

    show(count) {
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.index = -1;

      if (this.status && strings.searchResults) {
        this.status.textContent = strings.searchResults.replace('__count__', count);
      }
    }

    hide() {
      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.index = -1;
    }

    options() {
      return Array.prototype.slice.call(this.results.querySelectorAll('[role="option"]'));
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.hide();
        return;
      }

      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp' && event.key !== 'Enter') return;

      var options = this.options();
      if (!options.length || this.results.hidden) return;

      if (event.key === 'Enter') {
        if (this.index > -1) {
          event.preventDefault();
          options[this.index].click();
        }
        return;
      }

      event.preventDefault();
      this.index += event.key === 'ArrowDown' ? 1 : -1;
      if (this.index < 0) this.index = options.length - 1;
      if (this.index >= options.length) this.index = 0;

      options.forEach(function (option, i) {
        option.classList.toggle('is-active', i === this.index);
      }, this);

      options[this.index].scrollIntoView({ block: 'nearest' });
    }

    onDocumentClick(event) {
      if (!this.contains(event.target)) this.hide();
    }
  }

  /* ------------------------------------------------------- variant picker - */

  /**
   * Swatch and pill variant selection.
   *
   * The full variant list is embedded as JSON by the product section, so
   * matching is a local lookup — no request per selection. Availability for
   * each option value is recomputed on every change so combinations that do
   * not exist are visibly struck through and unselectable.
   */
  class KtfVariantPicker extends HTMLElement {
    connectedCallback() {
      var data = this.querySelector('[data-variant-json]');
      if (!data) return;

      try {
        this.variants = JSON.parse(data.textContent);
      } catch (error) {
        return;
      }

      this.form = document.getElementById(this.getAttribute('data-form'));
      this.idInput = this.form && this.form.querySelector('[name="id"]');
      this.sectionId = this.getAttribute('data-section');
      this.productUrl = this.getAttribute('data-url');

      this.addEventListener('change', this.onChange.bind(this));
      this.updateAvailability();
    }

    /** The currently checked value for each option position. */
    selection() {
      return Array.prototype.map.call(
        this.querySelectorAll('[data-option-position]'),
        function (group) {
          var checked = group.querySelector('input:checked');
          return checked ? checked.value : null;
        }
      );
    }

    match(selection) {
      return (
        this.variants.filter(function (variant) {
          return selection.every(function (value, i) {
            return value === null || variant.options[i] === value;
          });
        })[0] || null
      );
    }

    onChange() {
      var variant = this.match(this.selection());

      this.updateAvailability();

      if (this.idInput && variant) this.idInput.value = variant.id;

      if (variant && this.productUrl && window.history.replaceState) {
        window.history.replaceState({}, '', this.productUrl + '?variant=' + variant.id);
      }

      // The section listens for this and re-renders price, stock and gallery.
      this.dispatchEvent(
        new CustomEvent('variant:change', {
          bubbles: true,
          detail: { variant: variant }
        })
      );
    }

    /**
     * Marks each option value available or not, given the other selections.
     * A value is unavailable when no in-stock variant exists that combines it
     * with what is already chosen in the *other* positions.
     */
    updateAvailability() {
      var selection = this.selection();
      var self = this;

      this.querySelectorAll('[data-option-position]').forEach(function (group) {
        var position = parseInt(group.getAttribute('data-option-position'), 10);

        group.querySelectorAll('input[type="radio"]').forEach(function (input) {
          var probe = selection.slice();
          probe[position] = input.value;

          var candidates = self.variants.filter(function (variant) {
            return probe.every(function (value, i) {
              return value === null || variant.options[i] === value;
            });
          });

          var exists = candidates.length > 0;
          var available = candidates.some(function (variant) {
            return variant.available;
          });

          input.disabled = !exists;
          input.closest('label').classList.toggle('is-unavailable', !available);
          input.closest('label').classList.toggle('is-missing', !exists);
        });
      });
    }
  }

  /* -------------------------------------------------------------- gallery - */

  class KtfGallery extends HTMLElement {
    connectedCallback() {
      this.main = this.querySelector('[data-gallery-main]');
      this.thumbs = Array.prototype.slice.call(this.querySelectorAll('[data-gallery-thumb]'));

      this.thumbs.forEach(
        function (thumb, i) {
          thumb.addEventListener(
            'click',
            function (event) {
              event.preventDefault();
              this.select(i);
            }.bind(this)
          );
        }.bind(this)
      );

      this.addEventListener('keydown', this.onKeydown.bind(this));

      // Selecting a variant moves the gallery to that variant's image.
      var picker = document.querySelector('ktf-variant-picker');
      if (picker) {
        picker.addEventListener(
          'variant:change',
          function (event) {
            var variant = event.detail.variant;
            if (!variant || variant.featured_media_id == null) return;

            var index = this.thumbs.findIndex(function (thumb) {
              return thumb.getAttribute('data-media-id') === String(variant.featured_media_id);
            });
            if (index > -1) this.select(index);
          }.bind(this)
        );
      }
    }

    select(index) {
      var slides = this.querySelectorAll('[data-gallery-slide]');
      if (!slides[index]) return;

      slides.forEach(function (slide, i) {
        slide.hidden = i !== index;
      });

      this.thumbs.forEach(function (thumb, i) {
        thumb.classList.toggle('is-active', i === index);
        thumb.setAttribute('aria-current', i === index ? 'true' : 'false');
      });

      this.index = index;
    }

    onKeydown(event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (!event.target.closest('[data-gallery-thumb]')) return;

      event.preventDefault();
      var next = (this.index || 0) + (event.key === 'ArrowRight' ? 1 : -1);
      if (next < 0) next = this.thumbs.length - 1;
      if (next >= this.thumbs.length) next = 0;

      this.select(next);
      this.thumbs[next].focus();
    }
  }

  /* --------------------------------------------------------------- modal -- */

  /**
   * A generic dialog used for the size chart and the image lightbox.
   * Opened by any `[data-modal-open="<id>"]` button anywhere on the page.
   */
  class KtfModal extends HTMLElement {
    connectedCallback() {
      this.querySelectorAll('[data-modal-close]').forEach(
        function (button) {
          button.addEventListener('click', this.close.bind(this));
        }.bind(this)
      );

      this.addEventListener('click', function (event) {
        if (event.target === this) this.close();
      });

      document.addEventListener(
        'keydown',
        function (event) {
          if (event.key === 'Escape' && !this.hidden) this.close();
        }.bind(this)
      );
    }

    open() {
      this.hidden = false;
      lockScroll(true);
      this._release = trapFocus(this, this.querySelector('[data-modal-close]'));
    }

    close() {
      if (this.hidden) return;
      this.hidden = true;
      lockScroll(false);
      if (this._release) {
        this._release();
        this._release = null;
      }
    }
  }

  document.addEventListener('click', function (event) {
    var opener = event.target.closest('[data-modal-open]');
    if (!opener) return;

    var modal = document.getElementById(opener.getAttribute('data-modal-open'));
    if (!modal || typeof modal.open !== 'function') return;

    event.preventDefault();

    // The lightbox shows whichever image was clicked.
    var source = opener.getAttribute('data-modal-image');
    if (source) {
      var target = modal.querySelector('[data-modal-image-target]');
      if (target) {
        target.src = source;
        target.alt = opener.getAttribute('data-modal-alt') || '';
      }
    }

    modal.open();
  });

  /* -------------------------------------------------------- product form -- */

  class KtfProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.button = this.querySelector('[data-add-button]');
      this.error = this.querySelector('[data-add-error]');
      if (!this.form) return;

      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    onSubmit(event) {
      // Without the drawer the plain form post to /cart/add is the right
      // behaviour, so only intercept when there is a drawer to open.
      if (Kutify.cartType !== 'drawer') return;

      event.preventDefault();
      this.setError('');
      this.button.setAttribute('aria-disabled', 'true');

      Cart.add(new FormData(this.form))
        .then(
          function () {
            CartDrawer.open();
          }.bind(this)
        )
        .catch(
          function (error) {
            this.setError(error.message);
          }.bind(this)
        )
        .finally(
          function () {
            this.button.removeAttribute('aria-disabled');
          }.bind(this)
        );
    }

    setError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = !message;
    }
  }

  /* ------------------------------------------------------------- marquee -- */

  /**
   * The announcement ticker.
   *
   * The CSS already animates on its own, so this is an enhancement, not a
   * requirement — it does two things the stylesheet cannot:
   *
   *   1. Tops the group up with more copies when the text is short, so a
   *      narrow announcement on a wide screen never leaves a visible gap.
   *   2. Derives the duration from the measured width, so the bar travels at a
   *      constant px/second whatever the copy length. A fixed duration would
   *      make a long notice race and a short one crawl.
   */
  class KtfMarquee extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-marquee-track]');
      this.group = this.querySelector('[data-marquee-group]');
      if (!this.track || !this.group) return;

      // Honour the user's motion preference: leave it parked and unmeasured.
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this.speed = parseFloat(this.getAttribute('data-speed')) || 45;
      this.layout();

      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(debounce(this.layout.bind(this), 150));
        this.observer.observe(this);
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    layout() {
      var clone = this.track.children[1];
      if (!clone) return;

      // Top the first group up until it is at least as wide as the viewport,
      // capped so a pathologically short string cannot loop forever.
      var guard = 0;
      var seed = Array.prototype.map.call(this.group.children, function (child) {
        return child.cloneNode(true);
      });

      while (this.group.scrollWidth < this.offsetWidth && guard < 20) {
        seed.forEach(
          function (child) {
            var copy = child.cloneNode(true);
            copy.setAttribute('aria-hidden', 'true');
            this.group.appendChild(copy);
          }.bind(this)
        );
        guard += 1;
      }

      // The second group must mirror the first exactly, or the seam shows.
      clone.innerHTML = this.group.innerHTML;
      clone.setAttribute('aria-hidden', 'true');

      var width = this.group.scrollWidth;
      if (width > 0) {
        this.style.setProperty('--ktf-marquee-duration', width / this.speed + 's');
      }
    }
  }

  /* ------------------------------------------------- variant → page sync -- */

  /*
   * Keeps the buy column in step with the selected variant. Prices arrive
   * pre-formatted from Liquid, so nothing here has to know about currency.
   */
  document.addEventListener('variant:change', function (event) {
    var variant = event.detail.variant;

    // Price, button and label each appear twice once the floating bar is on
    // the page — in the buy box and in the bar — so these are collections.
    var prices = document.querySelectorAll('[data-price]');
    var buttons = document.querySelectorAll('[data-add-button]');
    var labels = document.querySelectorAll('[data-add-label]');
    var compare = document.querySelector('[data-compare-price]');
    var sku = document.querySelector('[data-sku]');
    var availability = document.querySelector('[data-availability]');

    // No variant matches the chosen combination at all.
    if (!variant) {
      buttons.forEach(function (b) { b.disabled = true; });
      labels.forEach(function (l) { l.textContent = strings.unavailable; });
      if (availability) availability.textContent = strings.unavailable;
      return;
    }

    prices.forEach(function (p) { p.textContent = variant.price; });

    if (compare) {
      compare.textContent = variant.compare_at_price || '';
      compare.hidden = !variant.compare_at_price;
    }

    if (sku) sku.textContent = variant.sku || '';

    if (availability) {
      availability.textContent = variant.available ? availability.dataset.inStock || availability.textContent : strings.soldOut;
    }

    buttons.forEach(function (b) { b.disabled = !variant.available; });

    labels.forEach(function (label) {
      label.textContent = variant.available
        ? (label.dataset.template || '').replace('__price__', variant.price) || variant.price
        : strings.soldOut;
    });

  });

  /* --------------------------------------------- product recommendations -- */

  /*
   * Shopify's recommendations are only available through their own endpoint,
   * so the section is fetched after load rather than blocking the page.
   */
  (function initRecommendations() {
    var host = document.querySelector('[data-recommendations]');
    if (!host) return;

    var url = host.getAttribute('data-url');
    if (!url) return;

    fetch(url)
      .then(function (response) {
        if (!response.ok) throw new Error('recommendations unavailable');
        return response.text();
      })
      .then(function (html) {
        var holder = document.createElement('div');
        holder.innerHTML = html;

        var fresh = holder.querySelector('[data-recommendations]');
        // Nothing to recommend is a normal outcome — leave the page as it is
        // rather than showing an empty heading.
        if (fresh && fresh.querySelector('.ktf-card')) host.replaceWith(fresh);
      })
      .catch(function () {
        /* A failed recommendation fetch must never break the product page. */
      });
  })();

  /* -------------------------------------------------------------- facets -- */

  /*
   * Collection filtering is a plain GET form, so it already works without any
   * of this. The only job here is to remove the "Kohalda" step: changing a
   * chip or the sort order submits immediately, and empty price fields are
   * dropped so they do not clutter the resulting URL.
   */
  (function initFacets() {
    var form = document.querySelector('[data-facets]');
    if (!form) return;

    var apply = form.querySelector('[data-facet-apply]');
    if (apply) apply.hidden = true;

    function submit() {
      // A blank price bound is not a filter — leave it out of the query.
      form.querySelectorAll('input[type="number"]').forEach(function (input) {
        input.disabled = input.value === '';
      });
      form.submit();
    }

    form.addEventListener('change', function (event) {
      if (event.target.type === 'number') return;
      submit();
    });

    form.addEventListener(
      'input',
      debounce(function (event) {
        if (event.target.type === 'number') submit();
      }, 700)
    );
  })();

  /* --------------------------------------------------------------- reveal - */

  /*
   * Sections fade in as they are scrolled to.
   *
   * The hiding class is applied from here rather than from the markup, so with
   * JavaScript off, or if this file fails, every element is simply visible —
   * nothing is ever hidden by a stylesheet that might not get undone. Only
   * elements below the fold are touched, so the first screenful paints at once
   * and nothing flashes.
   *
   * The observer alone is not enough. Jump to the end of the page — a fragment
   * link, a restored scroll position, End, a fast flick — and everything in
   * between goes straight from below the viewport to above it without ever
   * intersecting, so it would stay invisible for good. The sweep below catches
   * anything the viewport has already passed, which makes stuck-invisible
   * content impossible.
   */
  (function reveal() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var main = document.querySelector('main');
    if (!main) return;

    var pending = [].slice.call(
      main.querySelectorAll('.shopify-section > section, .ktf-grid > li, .ktf-rejects__grid > div')
    );
    if (!pending.length) return;

    var fold = window.innerHeight;
    pending = pending.filter(function (el) {
      if (el.getBoundingClientRect().top < fold) return false;
      el.classList.add('ktf-reveal');
      return true;
    });
    if (!pending.length) return;

    // Cards in a row arrive in sequence rather than all at once.
    [].slice.call(main.querySelectorAll('.ktf-grid, .ktf-rejects__grid')).forEach(function (grid) {
      [].slice.call(grid.children).forEach(function (child, i) {
        if (child.classList.contains('ktf-reveal')) {
          child.style.transitionDelay = Math.min(i, 5) * 60 + 'ms';
        }
      });
    });

    var observer =
      'IntersectionObserver' in window
        ? new IntersectionObserver(
            function (entries) {
              entries.forEach(function (entry) {
                if (entry.isIntersecting) show(entry.target);
              });
            },
            { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
          )
        : null;

    function show(el) {
      el.classList.add('is-revealed');
      if (observer) observer.unobserve(el);
      var i = pending.indexOf(el);
      if (i > -1) pending.splice(i, 1);
      if (!pending.length) window.removeEventListener('scroll', onScroll);
    }

    function sweep() {
      var limit = window.innerHeight * 0.92;
      pending.slice().forEach(function (el) {
        if (el.getBoundingClientRect().top < limit) show(el);
      });
    }

    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        sweep();
      });
    }

    if (observer) pending.forEach(function (el) { observer.observe(el); });
    window.addEventListener('scroll', onScroll, { passive: true });
    sweep();
  })();

  /*
   * On a pointer device the register rows light up under the cursor. A phone
   * has no cursor, so the scroll position stands in for one: whichever row is
   * nearest the middle of the screen carries the same `is-lit` state, and the
   * highlight walks down the list as you scroll.
   *
   * Exactly one row is lit at a time. The observer narrows the field to the
   * rows crossing the centre band, then the nearest of those wins — otherwise
   * two short rows sharing the band would both light up. Nothing is wired at
   * all on a device that has hover, or on a page without these rows.
   */
  (function () {
    if (!('IntersectionObserver' in window)) return;
    if (window.matchMedia && window.matchMedia('(hover: hover)').matches) return;

    var rows = [].slice.call(
      document.querySelectorAll('.ktf-index__row--link, .ktf-lore__row--link')
    );
    if (!rows.length) return;

    var near = [];
    var lit = null;

    function pick() {
      var middle = window.innerHeight / 2;
      var best = null;
      var bestGap = Infinity;

      near.forEach(function (row) {
        var box = row.getBoundingClientRect();
        var gap = Math.abs(box.top + box.height / 2 - middle);
        if (gap < bestGap) {
          bestGap = gap;
          best = row;
        }
      });

      if (best === lit) return;
      if (lit) lit.classList.remove('is-lit');
      lit = best;
      if (lit) lit.classList.add('is-lit');
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var i = near.indexOf(entry.target);
          if (entry.isIntersecting && i === -1) near.push(entry.target);
          if (!entry.isIntersecting && i > -1) near.splice(i, 1);
        });
        pick();
      },
      // Candidates are the rows overlapping the middle 40% of the screen.
      { rootMargin: '-30% 0px -30% 0px', threshold: 0 }
    );

    rows.forEach(function (row) {
      observer.observe(row);
    });

    /*
     * The observer only fires on the way into and out of the band, but the
     * nearest row changes continuously in between, so the choice is refreshed
     * on scroll too — and only while something is actually in the band.
     */
    var lighting = false;
    window.addEventListener(
      'scroll',
      function () {
        if (lighting || !near.length) return;
        lighting = true;
        window.requestAnimationFrame(function () {
          lighting = false;
          pick();
        });
      },
      { passive: true }
    );
  })();

  /*
   * The size table lives on its own page, so there is one copy of it. The
   * product page links straight there — that is the whole feature with the
   * script off — and this borrows the table into the modal so a shopper
   * sizing up a shirt never leaves the product.
   *
   * Fetched once, on the first open. If it fails the modal still holds the
   * link out, and the flag is reset so the next open tries again.
   */
  (function () {
    var opener = document.querySelector('[data-size-chart-url]');
    var body = document.querySelector('[data-size-chart-body]');
    if (!opener || !body || !window.fetch) return;

    var loading = false;
    var loaded = false;

    opener.addEventListener('click', function () {
      if (loading || loaded) return;
      loading = true;

      fetch(opener.getAttribute('data-size-chart-url'), { credentials: 'same-origin' })
        .then(function (response) {
          if (!response.ok) throw new Error('size chart unavailable');
          return response.text();
        })
        .then(function (html) {
          var holder = document.createElement('div');
          holder.innerHTML = html;

          // The table and the note under it; the page's own heading is left
          // behind, because the modal already carries a title.
          var table = holder.querySelector('.ktf-sizes__scroll');
          if (!table) throw new Error('no table in page');
          var note = holder.querySelector('.ktf-sizes__note');

          body.textContent = '';
          body.appendChild(table);
          if (note) body.appendChild(note);
          loaded = true;
        })
        .catch(function () {
          /* The link out is still sitting in the modal. */
        })
        .finally(function () {
          loading = false;
        });
    });
  })();

  /*
   * The floating add to cart shows once the real buy row has left the screen,
   * and parks itself off the bottom again when it comes back. The markup
   * ships with `hidden` set, which the element clears on connect — so with
   * the script off the bar never appears and the buy box below is the only
   * add to cart, rather than an empty bar pinned to the viewport.
   */
  class KtfStickyAtc extends HTMLElement {
    connectedCallback() {
      var anchor = document.querySelector('.ktf-buy__row');
      if (!anchor || !('IntersectionObserver' in window)) return;

      this.hidden = false;
      this.setShown(false);

      new IntersectionObserver(
        function (entries) {
          this.setShown(!entries[0].isIntersecting);
        }.bind(this),
        // A little tolerance at the foot, so the bar does not flicker in and
        // out while the buy row sits on the edge of the viewport.
        { rootMargin: '0px 0px -15% 0px' }
      ).observe(anchor);
    }

    setShown(on) {
      if (on === this.shown) return;
      this.shown = on;
      this.classList.toggle('is-shown', on);
      // Parked off screen it must not take focus or be read out, or the page
      // has two add to cart buttons as far as assistive tech is concerned.
      this.setAttribute('aria-hidden', on ? 'false' : 'true');
      this.querySelectorAll('button').forEach(function (b) {
        b.tabIndex = on ? 0 : -1;
      });
    }
  }

  /* ------------------------------------------------------------- register - */

  customElements.define('ktf-header', KtfHeader);
  customElements.define('ktf-marquee', KtfMarquee);
  customElements.define('ktf-search', KtfSearch);
  customElements.define('ktf-variant-picker', KtfVariantPicker);
  customElements.define('ktf-gallery', KtfGallery);
  customElements.define('ktf-modal', KtfModal);
  customElements.define('ktf-product-form', KtfProductForm);
  customElements.define('ktf-sticky-atc', KtfStickyAtc);

  // `ktf-cart-drawer` is deliberately a plain element: it is replaced wholesale
  // on every cart change, and the delegated handlers above already cover it.

  /* --------------------------------------------------------------- eggs --- */

  /*
   * Behaviours the register does not advertise. Each finds its own hook and
   * returns at once when the page does not carry it, so a page with none of
   * them pays for nothing but the calls. Every one is wrapped: an easter egg
   * must never be the reason something stops working.
   *
   * Marked in Liquid with data-egg rather than matched on copy, so rewording a
   * tile or a heading cannot quietly unhook one.
   */

  var EGG = (window.Kutify && window.Kutify.strings) || {};

  function egg(fn) {
    try {
      fn();
    } catch (e) {
      /* The office does not record its own failures. */
    }
  }

  function motionOk() {
    return !window.matchMedia || !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // A finding, printed under the thing it is a finding about.
  function eggNote(host, text) {
    if (!text) return;
    var el = host.querySelector('[data-egg-note]');
    if (!el) {
      el = document.createElement('p');
      el.className = 'ktf-egg-note';
      el.setAttribute('data-egg-note', '');
      el.setAttribute('role', 'status');
      host.appendChild(el);
    }
    el.textContent = text;
  }

  // For findings with nowhere sensible to sit.
  function eggToast(text) {
    if (!text) return;
    var el = document.createElement('p');
    el.className = 'ktf-egg-toast';
    el.setAttribute('role', 'status');
    el.textContent = text;
    document.body.appendChild(el);
    setTimeout(function () {
      el.remove();
    }, 3200);
  }

  /*
   * Typed sequences. Keystrokes inside a field are typing, not a summons, so
   * inputs are left alone. A wrong key restarts the count rather than killing
   * it, so "kaakaan" still works.
   */
  function onSequence(seq, run) {
    var at = 0;
    document.addEventListener('keydown', function (e) {
      var t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;

      var key = (e.key || '').toLowerCase();
      at = key === seq[at] ? at + 1 : key === seq[0] ? 1 : 0;
      if (at === seq.length) {
        at = 0;
        run();
      }
    });
  }

  /* The sitting that never adjourned, counted from the day it opened. */
  function eggSitting() {
    var el = document.querySelector('[data-egg="sitting"]');
    if (!el || !EGG.sittingDays) return;

    var printed = el.textContent.trim();
    var since = new Date(el.getAttribute('data-since') + 'T00:00:00');
    var showing = false;

    el.addEventListener('click', function () {
      showing = !showing;
      if (!showing) {
        el.textContent = printed;
        return;
      }
      var days = Math.floor((Date.now() - since.getTime()) / 86400000);
      el.textContent = EGG.sittingDays.replace('__count__', days.toLocaleString('et-EE'));
    });
  }

  /*
   * Form 51-K, on the tile that reports how many appeals have ever been
   * upheld. The count is not shown: knowing you are on click 38 would spoil
   * the only joke here.
   *
   * Bound to the label rather than the tile, because the figure itself is a
   * link to the archive and that has to keep working.
   */
  function eggAppeal() {
    var tile = document.querySelector('[data-egg="appeal"]');
    if (!tile) return;

    var label = tile.querySelector('.ktf-statband__label');
    var figure = tile.querySelector('.ktf-stats__num');
    if (!label || !figure) return;

    // The finding goes in the <dd>: a <dl> > <div> may hold only dt and dd.
    var host = tile.querySelector('.ktf-statband__dd') || tile;
    var clicks = 0;
    var settling = false;

    label.addEventListener('click', function () {
      if (settling) return;
      clicks += 1;
      if (clicks < 51) return;

      clicks = 0;
      settling = true;
      var stood = figure.textContent;
      figure.textContent = '1';
      eggNote(host, EGG.appealGranted);

      setTimeout(function () {
        figure.textContent = stood;
        eggNote(host, EGG.appealCorrected);
        settling = false;
      }, 3000);
    });
  }

  /*
   * Kaksikloendus. The Lahknevus header re-counts the leeches; the recorded
   * column then reapplies §3 from the procedure printed further down the same
   * page and keeps whichever figure is the more favourable. A second count is
   * all §3 allows, so the third request is refused.
   *
   * Columns are positional: period, first count, second count, difference,
   * recorded.
   */
  function eggRecount() {
    var section = document.querySelector('[data-egg="recount"]');
    if (!section) return;

    var table = section.querySelector('.ktf-sizes__table');
    if (!table) return;

    var heads = table.querySelectorAll('thead th');
    if (heads.length < 5) return;

    var counted = 0;

    heads[3].addEventListener('click', function () {
      counted += 1;
      if (counted > 1) {
        eggNote(section, EGG.recountRefused);
        return;
      }

      table.querySelectorAll('tbody tr').forEach(function (row) {
        var cells = row.querySelectorAll('td');
        if (cells.length < 4) return;

        var first = parseInt(cells[0].textContent, 10);
        if (isNaN(first)) return;

        var second = first + Math.round((Math.random() - 0.5) * 60);
        var diff = second - first;

        cells[1].textContent = String(second);
        cells[2].textContent = diff === 0 ? '0' : diff > 0 ? '+' + diff : '−' + Math.abs(diff);
        cells[3].textContent = String(Math.max(first, second));
      });

      eggNote(section, EGG.recountDone);
    });
  }

  /* The fourth founding document, on the fourth click of the footnote that
     says it was never found. */
  function eggLostFile() {
    var footnote = document.querySelector('[data-egg="lost-file"]');
    var file = document.querySelector('[data-file04]');
    if (!footnote || !file) return;

    var attempts = 0;

    footnote.addEventListener('click', function () {
      attempts += 1;
      if (attempts < 4) return;

      file.hidden = false;
      file.scrollIntoView({ behavior: motionOk() ? 'smooth' : 'auto', block: 'center' });
    });
  }

  /* Reception closes at five. Ruum 207 does not. */
  function eggAfterHours() {
    var el = document.querySelector('[data-egg="hours"]');
    if (!el || !EGG.afterHours) return;

    var hour = new Date().getHours();
    if (hour >= 4 && hour < 17) return;

    el.textContent = el.textContent.trim() + ' · ' + EGG.afterHours;
  }

  /* The register code is not editable. It can be edited four times. */
  function eggRegistry() {
    var el = document.querySelector('[data-egg="registry"]');
    if (!el) return;

    var printed = el.textContent.trim();
    var digits = printed.match(/(\d+)\s*$/);
    if (!digits) return;

    var base = parseInt(digits[1], 10);
    var width = digits[1].length;
    var step = 0;

    el.addEventListener('click', function () {
      step += 1;
      if (step >= 5) {
        step = 0;
        el.textContent = printed;
        eggToast(EGG.registryLocked);
        return;
      }

      var next = String(base + step);
      while (next.length < width) next = '0' + next;
      el.textContent = printed.replace(/\d+\s*$/, next);
    });
  }

  /* Fresh grounds for refusal, dealt from a deeper pool than the four on show. */
  function eggReasons() {
    var source = document.querySelector('script[data-egg="reason-pool"]');
    var list = document.querySelector('.ktf-reject__reasons');
    var stamp = document.querySelector('.ktf-reject__stamp');
    if (!source || !list || !stamp) return;

    var pool;
    try {
      pool = JSON.parse(source.textContent);
    } catch (e) {
      return;
    }
    if (!pool || pool.length < 2) return;

    pool = pool
      .map(function (line) {
        return String(line).trim();
      })
      .filter(Boolean);

    /*
     * The stamp is decorative — base.css gives it pointer-events: none so it
     * cannot swallow clicks meant for the card underneath it. It is made
     * clickable here rather than there, so it stays inert on every card that
     * has no pool behind it.
     */
    stamp.style.pointerEvents = 'auto';

    var howMany = list.children.length || 4;

    stamp.addEventListener('click', function () {
      var dealt = pool
        .slice()
        .sort(function () {
          return Math.random() - 0.5;
        })
        .slice(0, howMany);

      list.textContent = '';
      dealt.forEach(function (text) {
        var item = document.createElement('li');
        item.className = 'ktf-reject__reason';

        var mark = document.createElement('span');
        mark.className = 'ktf-reject__x';
        mark.setAttribute('aria-hidden', 'true');
        mark.textContent = '✕';

        var body = document.createElement('span');
        body.className = 'ktf-reject__reason-text';
        body.textContent = text;

        item.appendChild(mark);
        item.appendChild(body);
        list.appendChild(item);
      });
    });
  }

  /* A notice for anyone who opens the console. */
  function eggConsole() {
    if (!EGG.eggConsole || !window.console || !console.log) return;
    console.log(EGG.eggConsole);
  }

  /* Toimik 03, crossing the page at its own pace. */
  function eggLeech() {
    if (document.querySelector('.ktf-egg-leech')) return;

    var el = document.createElement('div');
    el.className = 'ktf-egg-leech' + (motionOk() ? '' : ' is-still');
    el.setAttribute('aria-hidden', 'true');
    el.innerHTML =
      '<svg viewBox="0 0 64 16" width="64" height="16" focusable="false">' +
      '<ellipse cx="10" cy="8" rx="10" ry="6"/><ellipse cx="22" cy="8" rx="9" ry="6.5"/>' +
      '<ellipse cx="34" cy="8" rx="9" ry="6"/><ellipse cx="45" cy="8" rx="8" ry="5"/>' +
      '<ellipse cx="55" cy="8" rx="6" ry="3.5"/></svg>';

    document.body.appendChild(el);
    setTimeout(
      function () {
        el.remove();
      },
      motionOk() ? 13000 : 2600
    );
  }

  /* The stamp, applied to nothing in particular. */
  function eggStamp() {
    if (document.querySelector('.ktf-egg-stamp')) return;

    var el = document.createElement('div');
    el.className = 'ktf-egg-stamp';
    el.setAttribute('aria-hidden', 'true');
    el.textContent = EGG.eggStamp || '';
    document.body.appendChild(el);

    setTimeout(function () {
      el.remove();
    }, 2600);
  }

  /*
   * The archive's two visible behaviours. Not hidden — a sort control that is
   * invisible refuses nobody — but they live here for the helpers above.
   */

  function archiveSort() {
    var row = document.querySelector('[data-archive-sort]');
    if (!row) return;

    row.addEventListener('click', function (e) {
      if (!e.target || !e.target.closest || !e.target.closest('button')) return;
      eggNote(row, EGG.archiveSortRefused);
    });
  }

  /*
   * The last entry is filed when the reader reaches the bottom of the list.
   * Once only, and the card itself was written in Liquid — the script moves a
   * node and composes nothing.
   */
  function archiveGrowth() {
    var end = document.querySelector('[data-archive-end]');
    var template = document.querySelector('template[data-archive-extra]');
    var grid = document.querySelector('.ktf-rejects__grid');
    if (!end || !template || !grid || !('IntersectionObserver' in window)) return;

    var filed = false;
    var watcher = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (filed || !entry.isIntersecting) return;
        filed = true;
        watcher.disconnect();
        grid.appendChild(template.content.cloneNode(true));
      });
    });

    watcher.observe(end);
  }

  [
    archiveSort,
    archiveGrowth,
    eggSitting,
    eggAppeal,
    eggRecount,
    eggLostFile,
    eggAfterHours,
    eggRegistry,
    eggReasons,
    eggConsole
  ].forEach(egg);

  egg(function () {
    onSequence(['k', 'a', 'a', 'n'], eggLeech);
  });

  egg(function () {
    onSequence(
      [
        'arrowup', 'arrowup', 'arrowdown', 'arrowdown',
        'arrowleft', 'arrowright', 'arrowleft', 'arrowright',
        'b', 'a'
      ],
      eggStamp
    );
  });
})();
